// pages/organization/OrganizationDetailsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Sidebar from "../../components/layout/Sidebar";
import Header from "../../components/layout/Header";
import { Helmet } from "react-helmet";
import { httpRequest } from "../../utils/httpRequest";
import { useTranslation } from "react-i18next";
import { CDN_BASE_URL } from "../../config";

const Stat = ({ label, value, color = "indigo" }) => (
  <div className={`rounded-xl border bg-white p-4 shadow-sm`}>
    <div className="text-xs text-gray-500 mb-1">{label}</div>
    <div className={`text-xl font-extrabold text-${color}-600`}>{value}</div>
  </div>
);

const Card = ({ title, children, right }) => (
  <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-base font-bold text-gray-800">{title}</h3>
      {right}
    </div>
    {children}
  </div>
);

const OrganizationDetailsPage = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.includes("fa");
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [org, setOrg] = useState(null);
  const [error, setError] = useState(null);

  const token = JSON.parse(localStorage.getItem("user"))?.token;

  useEffect(() => {
    let abort = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await httpRequest("GET", `/organization/${id}`, null, {
          "x-access-token": token,
        });
        if (abort) return;
        setOrg(data?.data || null);
      } catch (e) {
        if (!abort)
          setError(e?.response?.data?.message || e.message || "Error");
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => {
      abort = true;
    };
  }, [id]);

  const isR = i18n.language.includes("fa");
  const mainContentClass = `flex-1 flex flex-col px-2 pb-5 lg:px-0 lg:pb-0 ${
    isR ? "md:mr-64" : "md:ml-64"
  }`;
  const mainSectionClass = `flex-1 overflow-y-auto p-4 md:p-6 bg-gray-100 rounded-[16px] ${
    isR ? "lg:me-3 lg:mb-3" : "lg:ms-3 lg:mb-3"
  }`;

  const users = org?.users || [];
  const categories = org?.categories || [];
  const exams = org?.exams || [];
  const stats = org?.stats || {
    userCount: users.length,
    categoryCount: categories.length,
    examCount: exams.length,
  };

  return (
    <div className="flex h-screen">
      <Helmet>
        <title>{org?.name ? `سازمان: ${org.name}` : "سازمان"}</title>
      </Helmet>

      <Sidebar />
      <div className={mainContentClass}>
        <Header />
        <main className={mainSectionClass}>
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {org?.logo ? (
                <img
                  src={CDN_BASE_URL + org.logo}
                  alt={org?.name}
                  className="w-10 h-10 rounded object-cover border"
                />
              ) : (
                <span className="w-10 h-10 rounded bg-gray-200 border inline-block" />
              )}
              <div>
                <h1 className="text-2xl font-bold text-gray-800">
                  {org?.name || "—"}
                </h1>
                <div className="text-sm text-gray-500">
                  مالک: {org?.owner?.name || org?.owner?.email || "—"}
                </div>
              </div>
            </div>
            <Link
              to="/organization/organizations"
              className="px-3 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 text-sm"
            >
              بازگشت
            </Link>
          </div>

          {/* Loading / Error */}
          {loading && (
            <div className="animate-pulse h-24 bg-white rounded-xl shadow-sm mb-4" />
          )}
          {error && (
            <div className="p-4 bg-red-50 text-red-700 rounded-xl shadow-sm mb-4">
              {error}
            </div>
          )}

          {/* Stats */}
          {org && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
              <Stat label="کاربران" value={stats.userCount} color="blue" />
              <Stat
                label="دسته‌بندی‌ها"
                value={stats.categoryCount}
                color="purple"
              />
              <Stat label="آزمون‌ها" value={stats.examCount} color="emerald" />
            </div>
          )}

          {/* Description */}
          {org?.description && (
            <Card title="توضیحات">
              <p className="text-sm text-gray-700 leading-6">
                {org.description}
              </p>
            </Card>
          )}

          {/* Users */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            <Card
              title="کاربران سازمان"
              right={
                <span className="text-xs text-gray-500">
                  {users.length} مورد
                </span>
              }
            >
              {users.length === 0 ? (
                <div className="text-sm text-gray-500">کاربری وجود ندارد.</div>
              ) : (
                <div className="divide-y">
                  {users.map((u) => (
                    <div
                      key={u._id}
                      className="py-2 flex items-center justify-between"
                    >
                      <div>
                        <div className="text-sm font-semibold text-gray-800">
                          {u.name || u.email || "—"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {u.email || u.phone || ""}
                        </div>
                      </div>
                      <div className="text-xs text-gray-400">
                        {u.tags?.length ? u.tags.join(", ") : ""}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Categories */}
            <Card
              title="دسته‌بندی‌ها"
              right={
                <span className="text-xs text-gray-500">
                  {categories.length} مورد
                </span>
              }
            >
              {categories.length === 0 ? (
                <div className="text-sm text-gray-500">
                  دسته‌بندی‌ای وجود ندارد.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {categories.map((c) => (
                    <div key={c._id} className="border rounded-lg p-3">
                      <div className="text-sm font-semibold text-gray-800">
                        {typeof c.title === "object"
                          ? c.title.fa || c.title.en || "—"
                          : c.title}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        سطح: {c.level ?? "—"}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Exams */}
          <div className="mt-6">
            <Card
              title="آزمون‌ها"
              right={
                <span className="text-xs text-gray-500">
                  {exams.length} مورد
                </span>
              }
            >
              {exams.length === 0 ? (
                <div className="text-sm text-gray-500">آزمونی وجود ندارد.</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {exams.map((ex) => (
                    <div key={ex._id} className="border rounded-xl p-3">
                      <div className="text-sm font-bold text-gray-800 mb-1">
                        {typeof ex.title === "object"
                          ? ex.title.fa || ex.title.en || "—"
                          : ex.title}
                      </div>
                      <div className="text-xs text-gray-500">
                        وضعیت: {ex.isPublished ? "منتشر شده" : "پیش‌نویس"}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        زمان کل: {ex.totalTimeMin ?? 0} دقیقه
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
};

export default OrganizationDetailsPage;
